/**
 * Trade lifecycle stage telemetry — click -> toast, broken down by stage, and
 * the three update lanes measured against each other.
 *
 * Why it exists: the operator sees the wallet balance move first, and then, a
 * few seconds later, the success toast and the tape row arrive together. Three
 * independent lanes with three different latencies, for a chain that put the
 * send and the swap in ONE block. Session 4 tried to settle that from reading
 * code and from server-side queries three times and was wrong three times.
 *
 * Rules this instrument obeys:
 *  - Every in-browser interval is a `performance.now()` delta. The user's wall
 *    clock is never subtracted from a chain timestamp: the only absolute stamp
 *    it sends is `click_at_server_ms`, taken from the calibrated server clock,
 *    and every chain-relative stage is assembled on the server from that.
 *  - Lanes are measured at PAINT (double requestAnimationFrame, the same
 *    pattern useRenderedTradeTelemetry proved), not at data arrival. "All three
 *    within 300ms" is a statement about what the operator sees.
 *  - The holdings lane is NOT polled. It re-reads on an EVENT — a balance push,
 *    a reconnect, a return to the tab — and its prices move continuously from
 *    the tape, with no schedule anywhere. (The comment that used to claim a
 *    fixed schedule here described a poller that never existed; `git log -S
 *    setInterval` over this module is empty.) Every reload attempt is still
 *    reported with the projector checkpoint it carried, so "our read was early"
 *    stays distinguishable from "the projector had not written yet".
 *  - It is fire-and-forget, never awaited, never throws, and carries no
 *    authority: nothing reads it back and no money path consults it.
 */

import { apiBase } from '../api/client'
import { getServerClockOffsetMs, serverNowMs } from './serverClock'
import { canonicalTonAddress } from './tonAddress'

/** One holdings row, reduced to what the lane needs. */
export type LifecycleHolding = { token_master: string; balance: string }

/**
 * The traded token's holdings amount, or null when the wallet has no row for
 * it — which is the normal state before a FIRST buy.
 *
 * The baseline and every later reading go through THIS function, so the two
 * sides can never disagree about address form. They are canonicalized because a
 * raw string compare against the wrong form silently never matches, and the
 * lane it would kill is the one the whole measurement is about.
 */
function holdingAmountFor(
  holdings: readonly LifecycleHolding[] | null | undefined,
  baseToken: string
): string | null {
  if (!holdings) return null
  const wanted = canonicalTonAddress(baseToken) ?? baseToken
  for (const item of holdings) {
    if ((canonicalTonAddress(item.token_master) ?? item.token_master) === wanted) {
      return item.balance
    }
  }
  return null
}

/** Stages recorded once each, as ms from the click. */
export type TradeLifecycleStage =
  /** The vault released the seed — step-up auth and decrypt are behind us. */
  | 'vault'
  /** The signable, freshly verified plan is in hand (click -> api). */
  | 'api_plan'
  /** The relay accepted the external message. */
  | 'broadcast'
  /** wallet.confirmation for the SEND transaction (inclusion, not the trade). */
  | 'inclusion'
  /** The tape row for this trade arrived over the websocket. */
  | 'tape'
  /** That tape row was painted. */
  | 'tape_paint'
  /** The success toast transitioned to confirmed. */
  | 'toast'
  /** The confirmed toast was painted. */
  | 'toast_paint'
  /** The wallet balance actually changed away from its pre-click value. */
  | 'balance'
  /** That new balance was painted. */
  | 'balance_paint'
  /** The holdings row for the traded token actually changed. */
  | 'holdings'
  /** That new holdings row was painted. */
  | 'holdings_paint'

export type TradeLifecycleOutcome = 'confirmed' | 'failed' | 'timeout'

/** One distinct wallet-balance value the session was shown after the click. */
type BalanceStep = {
  at_ms: number
  value: string
}

/**
 * One distinct PnL value the PANEL rendered after the click.
 *
 * The stage marks say WHEN the position lane moved; they carry no numbers, so
 * "PnL showed the wrong thing for about 100 ms and came back" could be
 * described but not measured. This is the same separator `balance_steps` is for
 * the balance: every distinct value, in order, with its time. A single step is
 * the requirement met; three steps name the two transitions to look at.
 */
type PositionStep = {
  at_ms: number
  value: string
}

/**
 * One wallet.balance push as it ARRIVED, before the lt guard ruled on it.
 *
 * The balance is the last lane still trailing the tape row, and the stage mark
 * alone cannot say why: a push that came late and a push that came early and
 * was refused look identical from the outside, and they have opposite fixes.
 * `applied=false` means WE dropped it; no push at all before the mark means the
 * wallet indexer had not sent one yet.
 */
type BalancePush = {
  at_ms: number
  lt: string
  applied: boolean
}

type HoldingsReload = {
  /** 1-based attempt number within this trade. */
  n: number
  /** The projector checkpoint the response carried. */
  event_id: number
  at_ms: number
  /** Whether this response already reflected the trade. */
  changed: boolean
}

export type TradeLifecycleRecord = {
  wallet: string
  pool: string
  side: string
  base_token: string
  /** Click instant on the SERVER clock. Never Date.now() unadjusted. */
  click_at_server_ms: number
  clock_offset_ms: number
  submitted_hash: string
  tx_hash: string
  /** Chain seconds of the confirming tape row, for the server-side join. */
  trade_chain_ts: number
  outcome: TradeLifecycleOutcome
  stages: Partial<Record<TradeLifecycleStage, number>>
  holdings_reloads: HoldingsReload[]
  /**
   * Every DISTINCT balance value shown after the click, in order. The stage
   * mark records only the first value that moved; the operator reported seeing
   * the balance land on one number and then correct itself, and a single mark
   * cannot tell an intermediate value from the final one.
   */
  balance_steps: BalanceStep[]
  /**
   * Every wallet.balance push seen after the click, with the lt it carried and
   * whether the ordering guard let it through. This is what separates "the
   * indexer was late" from "we refused an early one".
   */
  balance_pushes: BalancePush[]
  /** Every DISTINCT PnL value the panel rendered after the click, in order. */
  position_steps: PositionStep[]
  /**
   * Every DISTINCT price the panel valued the position at, in order.
   *
   * Once the canonical row has landed, exactly one input can still move PnL:
   * the price. The operator's own hypothesis, and the only one the code leaves
   * room for — a trade moves the pool it traded in, so the price right after it
   * is not the price at the click. Without this the two are indistinguishable
   * from "the overlay is still wrong".
   */
  price_steps: PositionStep[]
}

// A record that never completes must still be reported: "the lane never
// arrived" is the finding, and a dropped record looks like a healthy trade.
const HARD_FLUSH_MS = 45_000
// Once every lane has painted, wait for the stragglers, then send.
//
// One second was too short to see the thing criterion (4) is about. The
// projector writes the position 0.7-1.4 s AFTER the tape row, so the
// overlay -> canonical handover — the last transition the operator can see —
// landed just past the end of the record. Measured on a real sell: all three
// holdings polls inside the record came back `stale`, the operator reported a
// change about a second later, and `position_steps` had nothing to show for it.
//
// This is a diagnostic window, not a UI delay: it only decides how long the
// RECORD stays open, and nothing on screen waits for it.
const SETTLE_FLUSH_MS = 3_500
const MAX_HOLDINGS_RELOADS = 8
const MAX_BALANCE_STEPS = 8
const MAX_BALANCE_PUSHES = 10
const MAX_POSITION_STEPS = 8

const PAINT_LANES: readonly TradeLifecycleStage[] = [
  'toast_paint',
  'tape_paint',
  'balance_paint',
  'holdings_paint',
]

type ActiveRecorder = {
  startedAt: number
  record: TradeLifecycleRecord
  baselineBalanceNano: string | null
  baselineHoldingAmount: string | null
  holdingsAttempts: number
  hardTimer: number
  settleTimer: number
  flushed: boolean
}

let active: ActiveRecorder | null = null

function nowMs(): number {
  return typeof performance !== 'undefined' && typeof performance.now === 'function'
    ? performance.now()
    : Date.now()
}

function since(recorder: ActiveRecorder): number {
  return Math.max(0, Math.round(nowMs() - recorder.startedAt))
}

/**
 * Starts a recording for the trade this click is about to make. A previous
 * unfinished recording is flushed as a timeout rather than silently dropped.
 */
export function beginTradeLifecycle(input: {
  wallet: string | null | undefined
  pool: string
  side: string
  baseToken: string
  balanceNano: string | null
  holdings: readonly LifecycleHolding[] | null
}): void {
  try {
    if (active && !active.flushed) flushTradeLifecycle('timeout')
    const recorder: ActiveRecorder = {
      startedAt: nowMs(),
      record: {
        wallet: input.wallet ?? '',
        pool: input.pool,
        side: input.side,
        base_token: input.baseToken,
        click_at_server_ms: Math.floor(serverNowMs()),
        clock_offset_ms: Math.round(getServerClockOffsetMs()),
        submitted_hash: '',
        tx_hash: '',
        trade_chain_ts: 0,
        outcome: 'timeout',
        stages: {},
        holdings_reloads: [],
        balance_steps: [],
        balance_pushes: [],
        position_steps: [],
        price_steps: [],
      },
      baselineBalanceNano: input.balanceNano,
      baselineHoldingAmount: holdingAmountFor(input.holdings, input.baseToken),
      holdingsAttempts: 0,
      hardTimer: 0,
      settleTimer: 0,
      flushed: false,
    }
    recorder.hardTimer = windowSetTimeout(() => flushTradeLifecycle('timeout'), HARD_FLUSH_MS)
    active = recorder
  } catch {
    // A diagnostic that can throw into the money path is worse than none.
  }
}

/** True while a trade recording is open — cheap guard for hot callers. */
export function tradeLifecycleActive(): boolean {
  return active !== null && !active.flushed
}

/** Records the first occurrence of a stage. Later repeats are ignored. */
export function markTradeLifecycleStage(stage: TradeLifecycleStage): void {
  try {
    const recorder = active
    if (!recorder || recorder.flushed || recorder.record.stages[stage] !== undefined) return
    recorder.record.stages[stage] = since(recorder)
    maybeScheduleSettle(recorder)
  } catch {
    // ignored
  }
}

/**
 * Records a stage at PAINT: two animation frames put the observation after
 * React's commit and across a paint opportunity, so it lands on the surface the
 * operator can actually read.
 */
export function markTradeLifecyclePaint(stage: TradeLifecycleStage): void {
  try {
    const recorder = active
    if (!recorder || recorder.flushed || recorder.record.stages[stage] !== undefined) return
    if (typeof requestAnimationFrame !== 'function') {
      markTradeLifecycleStage(stage)
      return
    }
    requestAnimationFrame(() => {
      requestAnimationFrame(() => markTradeLifecycleStage(stage))
    })
  } catch {
    // ignored
  }
}

/** Binds the hash of the external message the relay accepted. */
export function noteTradeLifecycleSubmitted(submittedHash: string | null): void {
  try {
    if (!active || active.flushed || !submittedHash) return
    active.record.submitted_hash = submittedHash
  } catch {
    // ignored
  }
}

/** Binds the confirming trade: its tx hash and its chain second. */
export function noteTradeLifecycleTrade(txHash: string, chainTs: number | string): void {
  try {
    const recorder = active
    if (!recorder || recorder.flushed) return
    if (!recorder.record.tx_hash) recorder.record.tx_hash = txHash
    const seconds = Number(chainTs)
    if (Number.isFinite(seconds) && seconds > 0 && recorder.record.trade_chain_ts === 0) {
      recorder.record.trade_chain_ts = Math.floor(seconds)
    }
  } catch {
    // ignored
  }
}

/**
 * Offers the current wallet balance. The first value that differs from the
 * pre-click one marks the balance lane — an unchanged push is not the trade.
 * Returns true only on that first differing value, so the caller knows this is
 * the commit whose paint belongs to the balance lane.
 */
export function noteTradeLifecycleBalance(balanceNano: string | null): boolean {
  try {
    const recorder = active
    if (!recorder || recorder.flushed || balanceNano === null) return false
    const steps = recorder.record.balance_steps
    if (steps.at(-1)?.value !== balanceNano && steps.length < MAX_BALANCE_STEPS) {
      steps.push({ at_ms: since(recorder), value: balanceNano })
    }
    if (balanceNano === recorder.baselineBalanceNano) return false
    if (recorder.record.stages.balance !== undefined) return false
    markTradeLifecycleStage('balance')
    return true
  } catch {
    return false
  }
}

/**
 * Offers a tape row for the pending trade's OWN transaction.
 *
 * The confirmation path stops looking at tape rows the moment any lane wins, so
 * when the seqno backstop closes the toast first, the row that arrives after it
 * is never seen at all — and a record with no `tape` stage then reads as "no row
 * ever came", which is indistinguishable from "the row came late". That
 * difference decides whether the backstop should be closing toasts, so it has to
 * be observable.
 *
 * Matching is by transaction hash ONLY: the hash is bound the moment any lane
 * confirms, so this can attribute a late row without re-deciding anything the
 * confirmation guards already decided. It never confirms, refuses or delays a
 * trade — it only records.
 */
export function noteTradeLifecycleTapeRow(
  txHash: string | undefined,
  chainTs: number | string | undefined
): void {
  try {
    const recorder = active
    if (!recorder || recorder.flushed || !txHash) return
    if (recorder.record.tx_hash !== txHash) return
    if (recorder.record.stages.tape !== undefined) return
    noteTradeLifecycleTrade(txHash, chainTs ?? 0)
    markTradeLifecycleStage('tape')
    markTradeLifecyclePaint('tape_paint')
  } catch {
    // ignored
  }
}

/**
 * Offers one portfolio response. Every attempt is recorded with the projector
 * checkpoint it carried, so a quantized arrival time cannot be misread as the
 * projector's latency.
 */
export function noteTradeLifecycleHoldings(input: {
  holdings: readonly LifecycleHolding[]
  eventID: number
}): boolean {
  try {
    const recorder = active
    if (!recorder || recorder.flushed) return false
    const holdingAmount = holdingAmountFor(input.holdings, recorder.record.base_token)
    const changed = holdingAmount !== null && holdingAmount !== recorder.baselineHoldingAmount
    recorder.holdingsAttempts += 1
    if (recorder.record.holdings_reloads.length < MAX_HOLDINGS_RELOADS) {
      recorder.record.holdings_reloads.push({
        n: recorder.holdingsAttempts,
        event_id: Number.isFinite(input.eventID) ? Math.floor(input.eventID) : -1,
        at_ms: since(recorder),
        changed,
      })
    }
    if (!changed || recorder.record.stages.holdings !== undefined) return false
    // A projector answer can only be evidence of a trade the browser already
    // knows exists. Without that guard it reported the PREVIOUS trade: the
    // projector writes ~1s late, so a trade clicked while it is still catching
    // up starts with a baseline that its predecessor's write then moves, and
    // "changed" fires on data that has nothing to do with this trade.
    //
    // Measured, twice, on 2026-08-10 (records `bWxL2iwJ…` and `Qy52UXWg…`): the
    // holdings lane was marked at 1899 ms and 2137 ms while THIS trade's own
    // transaction only reached a block at 3207 ms and 2213 ms — earlier than the
    // event it claimed to be reporting. Those two marks are the entire reason
    // both sells showed a 3137 ms and 2120 ms lane spread; every trade whose
    // lane was marked by the rendered panel came in at 0-82 ms.
    //
    // The tape row (or, with no row, the confirmation) is the first moment this
    // trade is known to exist, so it is the earliest instant a holdings answer
    // can be attributed to it. This never delays a real mark: the rendered panel
    // marks the lane on confirmation, and the first mark wins.
    if (recorder.record.stages.tape === undefined && recorder.record.stages.toast === undefined) {
      return false
    }
    markTradeLifecycleStage('holdings')
    return true
  } catch {
    return false
  }
}

/**
 * The rendered position now shows this trade.
 *
 * The holdings lane used to be marked when a PROJECTOR answer arrived, because
 * that was the only thing that moved those cells. It no longer is: Holding and
 * PnL are computed from the token balance and the live price, and Bought/Sold
 * carry the trade's own amount, so the panel can show the trade while the
 * projector is still writing. An instrument that kept timing the projector
 * would report a lane the operator is already looking at as late.
 *
 * The reload journal is untouched and still records every projector answer with
 * its checkpoint — that is what tells the projector's latency apart from ours.
 */
export function noteTradeLifecyclePositionRendered(): boolean {
  try {
    const recorder = active
    if (!recorder || recorder.flushed) return false
    if (recorder.record.stages.holdings !== undefined) return false
    markTradeLifecycleStage('holdings')
    return true
  } catch {
    return false
  }
}

/**
 * Offers the PnL value the panel is rendering. Only values that DIFFER from the
 * previous one are recorded, so a steady number costs nothing.
 */
export function noteTradeLifecyclePositionValue(value: string | null): void {
  try {
    const recorder = active
    if (!recorder || recorder.flushed || value === null) return
    const steps = recorder.record.position_steps
    if (steps.at(-1)?.value === value) return
    if (steps.length >= MAX_POSITION_STEPS) return
    steps.push({ at_ms: since(recorder), value })
  } catch {
    // ignored
  }
}

/** Offers the price the panel is valuing the position at. */
export function noteTradeLifecyclePrice(value: string | null): void {
  try {
    const recorder = active
    if (!recorder || recorder.flushed || value === null) return
    const steps = recorder.record.price_steps
    if (steps.at(-1)?.value === value) return
    if (steps.length >= MAX_POSITION_STEPS) return
    steps.push({ at_ms: since(recorder), value })
  } catch {
    // ignored
  }
}

/**
 * Records a wallet.balance push exactly as it arrived, before the lt ordering
 * guard decides anything. Never influences that decision.
 */
export function noteTradeLifecycleBalancePush(lt: string, applied: boolean): void {
  try {
    const recorder = active
    if (!recorder || recorder.flushed) return
    if (recorder.record.balance_pushes.length >= MAX_BALANCE_PUSHES) return
    recorder.record.balance_pushes.push({ at_ms: since(recorder), lt, applied })
  } catch {
    // ignored
  }
}

/** Sends the record. Idempotent: the first outcome wins. */
export function flushTradeLifecycle(outcome: TradeLifecycleOutcome): void {
  try {
    const recorder = active
    if (!recorder || recorder.flushed) return
    recorder.flushed = true
    active = null
    windowClearTimeout(recorder.hardTimer)
    windowClearTimeout(recorder.settleTimer)
    recorder.record.outcome = outcome
    postTradeLifecycle(recorder.record)
  } catch {
    // ignored
  }
}

function maybeScheduleSettle(recorder: ActiveRecorder): void {
  if (recorder.settleTimer) return
  if (!PAINT_LANES.every((lane) => recorder.record.stages[lane] !== undefined)) return
  recorder.settleTimer = windowSetTimeout(() => flushTradeLifecycle('confirmed'), SETTLE_FLUSH_MS)
}

function postTradeLifecycle(record: TradeLifecycleRecord): void {
  try {
    void fetch(`${apiBase}/v1/telemetry/trade-lifecycle`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(record),
      keepalive: true,
    }).catch(() => undefined)
  } catch {
    // ignored
  }
}

// globalThis, not window: this module is also exercised outside a DOM, and a
// silently missing timer would turn the guaranteed flush into a dropped record.
function windowSetTimeout(fn: () => void, ms: number): number {
  if (typeof setTimeout !== 'function') return 0
  return setTimeout(fn, ms) as unknown as number
}

function windowClearTimeout(handle: number): void {
  if (!handle || typeof clearTimeout !== 'function') return
  clearTimeout(handle as unknown as ReturnType<typeof setTimeout>)
}

/** Test seam: drops any open recording without sending it. */
export function resetTradeLifecycleForTest(): void {
  if (active) {
    windowClearTimeout(active.hardTimer)
    windowClearTimeout(active.settleTimer)
  }
  active = null
}
